/**
 * Persistent cache using localStorage with TTL support.
 * Implements stale-while-revalidate pattern: returns stale data
 * immediately while background refresh happens.
 */

const CACHE_PREFIX = 'ftc_';

interface CacheEntry<T> {
  data: T;
  expiry: number;
  timestamp: number;
}

export const persistentCache = {
  set<T>(key: string, data: T, ttlMs: number = 30 * 60 * 1000): void {
    try {
      const entry: CacheEntry<T> = {
        data,
        expiry: Date.now() + ttlMs,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
    } catch {
      // Silently fail if localStorage is full — non-critical
    }
  },

  get<T>(key: string): { data: T; fresh: boolean; age: number } | null {
    try {
      const raw = localStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      const entry: CacheEntry<T> = JSON.parse(raw);
      return {
        data: entry.data,
        fresh: Date.now() < entry.expiry,
        age: Date.now() - entry.timestamp,
      };
    } catch {
      return null;
    }
  },

  delete(key: string): void {
    localStorage.removeItem(CACHE_PREFIX + key);
  },

  deletePattern(pattern: string): void {
    const keys = Object.keys(localStorage).filter(
      k => k.startsWith(CACHE_PREFIX) && k.includes(pattern)
    );
    keys.forEach(k => localStorage.removeItem(k));
  },

  clearAll(): void {
    Object.keys(localStorage)
      .filter(k => k.startsWith(CACHE_PREFIX))
      .forEach(k => localStorage.removeItem(k));
  },

  /**
   * Sweep old entries so the cache can't grow unbounded and eventually fill
   * the ~5MB localStorage quota — which would make set() silently fail and kill
   * the cache-first render for exactly the most loyal daily users. Called once
   * at startup.
   *
   * Eviction is by AGE only, never by TTL. The TTL just marks an entry as
   * stale (`fresh: false`) — stale entries are the whole point of
   * stale-while-revalidate: they paint the screen while fresh data loads.
   * Sweeping on TTL (30 min) emptied the cache before nearly every app open,
   * so the Dashboard fell back to a spinner every time.
   */
  sweepExpired(maxAgeMs: number = 8 * 24 * 60 * 60 * 1000): void {
    try {
      const now = Date.now();
      Object.keys(localStorage)
        .filter(k => k.startsWith(CACHE_PREFIX))
        .forEach(k => {
          try {
            const raw = localStorage.getItem(k);
            if (!raw) return;
            const entry = JSON.parse(raw) as CacheEntry<unknown>;
            const tooOld = typeof entry.timestamp !== 'number' || now - entry.timestamp > maxAgeMs;
            if (tooOld) localStorage.removeItem(k);
          } catch {
            // Corrupt entry — drop it
            localStorage.removeItem(k);
          }
        });
    } catch {
      // Non-critical
    }
  },
};
